import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WeeklyPost from '../../components/WeeklyPost/WeeklyPost';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './HymnDetailPage.module.css';
import config from '../../config/config';
import { HymnDetailData } from '../../types/types';
import '../../components/PortableTextStyles.css';

const HymnDetailPage: React.FC = () => {
  const { hymnId } = useParams<{ hymnId: string }>();
  const navigate = useNavigate();
  const [hymnDetail, setHymnDetail] = useState<HymnDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHymnDetail = async () => {
      setLoading(true);
      try {
        // Fetch hymn directly by ID
        const response = await axios.get(`${config.API_BASE_URL}/hymns/${hymnId}`);
        
        if (response.data) {
          // Transform the Sanity hymn post to our HymnDetailData format
          const hymn: HymnDetailData = {
            id: response.data.id,
            title: response.data.title,
            author: response.data.author,
            year: response.data.year,
            lyrics: response.data.lyrics,
            explanationPortableText: response.data.explanation
          };
          
          setHymnDetail(hymn);
          setError(null);
        } else {
          setError(`Hymn with ID "${hymnId}" not found.`);
        }
      } catch (error) {
        console.error('Error fetching hymn detail:', error);
        setError(`Failed to fetch hymn with ID "${hymnId}". Please try again later.`);
        
        // Fallback to mock data if enabled in config
        if (config.USE_MOCK_DATA) {
          // Mock data for development/testing
          const mockHymns: HymnDetailData[] = [
            {
              id: 1,
              title: 'Amazing Grace',
              author: 'John Newton',
              year: '1779',
              lyrics: 'Amazing grace! How sweet the sound,\nThat saved a wretch like me!\nI once was lost, but now am found,\nWas blind, but now I see.\n\n\'Twas grace that taught my heart to fear,\nAnd grace my fears relieved;\nHow precious did that grace appear\nThe hour I first believed!',
              explanationPortableText: [
                {
                  _type: 'block',
                  children: [
                    {
                      _type: 'span',
                      text: 'Written by John Newton (1779), this beloved hymn expresses the profound transformation of God\'s grace in a sinner\'s life. Newton, a former slave trader, experienced a dramatic conversion during a violent storm at sea in 1748.'
                    }
                  ]
                }
              ]
            },
            {
              id: 2,
              title: 'Great Is Thy Faithfulness',
              author: 'Thomas O. Chisholm',
              year: '1923',
              lyrics: 'Great is Thy faithfulness, O God my Father,\nThere is no shadow of turning with Thee;\nThou changest not, Thy compassions, they fail not;\nAs Thou hast been Thou forever wilt be.',
              explanationPortableText: [
                {
                  _type: 'block',
                  children: [
                    {
                      _type: 'span',
                      text: 'Composed by Thomas O. Chisholm (1923), this hymn celebrates God\'s unchanging character and daily faithfulness to His people.'
                    }
                  ]
                }
              ]
            },
            {
              id: 3,
              title: 'How Great Thou Art',
              author: 'Carl Boberg / Stuart K. Hine',
              year: '1885/1949',
              lyrics: 'O Lord my God, when I in awesome wonder\nConsider all the worlds Thy hands have made,\nI see the stars, I hear the rolling thunder,\nThy power throughout the universe displayed.',
              explanationPortableText: [
                {
                  _type: 'block',
                  children: [
                    {
                      _type: 'span',
                      text: 'Originally a Swedish poem by Carl Boberg (1885) translated by Stuart K. Hine (1949), this hymn expresses wonder at God\'s creation and redemptive work.'
                    }
                  ]
                }
              ]
            }
          ];
          
          const foundHymn = mockHymns.find(hymn => hymn.id === Number(hymnId));
          
          if (foundHymn) {
            setHymnDetail(foundHymn);
            setError(null);
          } else {
            setError(`Hymn with ID "${hymnId}" not found.`);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    if (hymnId) {
      fetchHymnDetail();
    } else {
      setError('No hymn ID provided.');
      setLoading(false);
    }
  }, [hymnId]);

  const handleBackClick = () => {
    navigate('/hymns');
  };

  if (loading) {
    return (
      <div className={styles['hymn-detail-page']}>
        <div className={styles['loading']}>Loading hymn details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['hymn-detail-page']}>
        <div className={styles['error']}>
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Hymns
          </button>
        </div>
      </div>
    );
  }

  if (!hymnDetail) {
    return (
      <div className={styles['hymn-detail-page']}>
        <div className={styles['error']}>
          <h2>Hymn Not Found</h2>
          <p>The requested hymn could not be found.</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Hymns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['hymn-detail-page']}>
      <div className={styles['hymn-header']}>
        <button className={styles['back-button']} onClick={handleBackClick}>
          ← Back to Hymns
        </button>
        <h1>{hymnDetail.title}</h1>
      </div>

      <div className={styles['hymn-verses']}>
        <h2>Lyrics</h2>
        <div className={styles['verse-text']}>
          <p>{hymnDetail.lyrics}</p>
        </div>
      </div>

      <div className={styles['hymn-teaching']}>
        <WeeklyPost
          title={`The Story Behind ${hymnDetail.title}`}
          date={hymnDetail.year || ''}
          content={hymnDetail.explanationPortableText}
          type="hymn"
          author={hymnDetail.author}
          references={[]}
        />
      </div>

      <ContactUs />
    </div>
  );
};

export default HymnDetailPage;
